import { useState } from "react";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { boolean, object, string } from "yup";

import type {
  GenericConfiguration,
  UpsertGenericInstanceRequest,
} from "@thalamos/aspire-common";
import { nonEmptyRequiredString } from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { routeFns } from "../../routePaths.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import {
  Button,
  FormLabel,
  HelperText,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";

const genericInstanceDetailsSchema = object({
  displayName: nonEmptyRequiredString
    .default("")
    .required("Please enter a display name"),
  systemName: nonEmptyRequiredString
    .default("")
    .required("Please enter a system name"),
  apiKey: nonEmptyRequiredString
    .default("")
    .required("Please enter the API key"),
  hmacSha512SecretKey: nonEmptyRequiredString
    .default("")
    .required("Please enter the HMAC-SHA512 secret key"),
  launchUrl: nonEmptyRequiredString
    .default("")
    .required("Please enter the launch URL")
    // Use the URL constructor rather than yup's `.url()`, whose regex rejects
    // hostless URLs like http://localhost:3000 (no TLD) — common in dev/demo.
    .test("valid-url", "Must be a valid URL", (value) => {
      if (!value) {
        return true;
      }
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    }),
  odsCode: string().default(""),
  rememberLoginEnabled: boolean().default(false),
});

function DeleteGenericInstanceButton({
  genericInstance,
}: {
  genericInstance: GenericConfiguration;
}) {
  const [confirmFn, setConfirmFn] = useState<{
    confirmFn: () => void;
    message: string;
  } | null>(null);
  const navigate = useNavigate();

  const deleteInstance = async () => {
    try {
      const result = await api.generic.instance.delete(
        genericInstance.instanceId,
      );

      if (result.status === 204) {
        renderSuccessToast({
          message: `Generic instance ${genericInstance.displayName} has been deleted`,
        });
        navigate(routeFns.adminGenericInstancesPage());
      } else {
        renderErrorToast({
          message: `Failed to remove generic instance: ${genericInstance.displayName} with error: ${result.data?.reason ?? "Unknown error"}`,
        });
      }
    } catch (error: unknown) {
      const reason =
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof (error as { response?: { data?: { reason?: string } } }).response
          ?.data?.reason === "string"
          ? (error as { response: { data: { reason: string } } }).response.data
              .reason
          : "Unknown error";
      renderErrorToast({
        message: `Failed to remove generic instance: ${genericInstance.displayName} with error: ${reason}`,
      });
    }
    setConfirmFn(null);
  };

  return (
    <>
      {confirmFn && (
        <ConfirmationModal
          message={confirmFn.message}
          confirmFn={confirmFn.confirmFn}
          closeFn={() => {
            setConfirmFn(null);
          }}
        />
      )}

      <Button
        onClick={() => {
          setConfirmFn({
            message: `Are you sure you want to delete this generic instance ${genericInstance.displayName}? This cannot be undone.`,
            confirmFn: () => {
              void deleteInstance();
            },
          });
        }}
        sx={{ ml: "1em" }}
        color={"error"}
        label={"Delete"}
      />
    </>
  );
}

function GenericInstanceEdit({
  genericInstanceId,
  genericInstance,
  refetchGenericInstance,
}: {
  genericInstanceId: string;
  genericInstance: GenericConfiguration | null;
  refetchGenericInstance: () => void;
}) {
  const initialData = {
    ...genericInstanceDetailsSchema.getDefault(),
    ...(genericInstance
      ? {
          displayName: genericInstance.displayName ?? "",
          systemName: genericInstance.systemName ?? "",
          apiKey: genericInstance.apiKey ?? "",
          hmacSha512SecretKey: genericInstance.hmacSha512SecretKey ?? "",
          launchUrl: genericInstance.launchUrl ?? "",
          odsCode: genericInstance.odsCode ?? "",
          rememberLoginEnabled: genericInstance.rememberLoginEnabled ?? false,
        }
      : {}),
  };

  return (
    <Formik<Omit<UpsertGenericInstanceRequest, "demographicsSource">>
      initialValues={initialData}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        const cleanedData: UpsertGenericInstanceRequest = {
          displayName: values.displayName.trim(),
          systemName: values.systemName.trim(),
          apiKey: values.apiKey.trim(),
          hmacSha512SecretKey: values.hmacSha512SecretKey.trim(),
          launchUrl: values.launchUrl.trim(),
          odsCode: values.odsCode?.trim() ? values.odsCode.trim() : null,
          // PDS is the only supported demographics source for now.
          demographicsSource: "pds",
          rememberLoginEnabled: values.rememberLoginEnabled ?? false,
        };
        if (genericInstance) {
          const result = await api.generic.instance.update(
            genericInstanceId,
            cleanedData,
          );
          if (result.status === 204) {
            refetchGenericInstance();
            renderSuccessToast({
              message: "Generic instance updated successfully",
            });
          } else {
            renderErrorToast({
              message: result.data?.reason ?? "Failed to update instance",
            });
          }
        } else {
          const result = await api.generic.instance.create(
            genericInstanceId,
            cleanedData,
          );
          if (result.status === 204) {
            refetchGenericInstance();
            renderSuccessToast({
              message: "Generic instance created successfully",
            });
          } else {
            renderErrorToast({
              message: result.data?.reason ?? "Failed to create instance",
            });
          }
        }
        setSubmitting(false);
      }}
      validationSchema={genericInstanceDetailsSchema}
    >
      {(formikData) => {
        const { values, setValues, errors, touched, submitForm, dirty } =
          formikData;
        return (
          <>
            <Box sx={{ maxWidth: "600px", marginBottom: "2em" }}>
              <FormLabel
                label={"Display name (*)"}
                error={touched.displayName && !!errors.displayName}
              />
              <TextField
                name={"displayName"}
                useFullWidth={true}
                value={values.displayName}
                onChange={(e: string) => {
                  void setValues({ ...values, displayName: e });
                }}
              />
              {errors.displayName && touched.displayName && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.displayName} />
                </Box>
              )}

              <FormLabel
                label={"System name (*)"}
                error={touched.systemName && !!errors.systemName}
              />
              <TextField
                name={"systemName"}
                useFullWidth={true}
                value={values.systemName}
                onChange={(e: string) => {
                  void setValues({ ...values, systemName: e });
                }}
              />
              {errors.systemName && touched.systemName && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.systemName} />
                </Box>
              )}

              <FormLabel
                label={"API Key (*)"}
                error={touched.apiKey && !!errors.apiKey}
              />
              <TextField
                name={"apiKey"}
                useFullWidth={true}
                value={values.apiKey}
                onChange={(e: string) => {
                  void setValues({ ...values, apiKey: e });
                }}
              />
              {errors.apiKey && touched.apiKey && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.apiKey} />
                </Box>
              )}

              <FormLabel
                label={"HMAC-SHA512 secret key (*)"}
                error={
                  touched.hmacSha512SecretKey && !!errors.hmacSha512SecretKey
                }
              />
              <TextField
                name={"hmacSha512SecretKey"}
                useFullWidth={true}
                value={values.hmacSha512SecretKey}
                onChange={(e: string) => {
                  void setValues({ ...values, hmacSha512SecretKey: e });
                }}
              />
              {errors.hmacSha512SecretKey && touched.hmacSha512SecretKey && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.hmacSha512SecretKey} />
                </Box>
              )}

              <FormLabel
                label={"Launch URL (*)"}
                error={touched.launchUrl && !!errors.launchUrl}
              />
              <TextField
                name={"launchUrl"}
                useFullWidth={true}
                value={values.launchUrl}
                onChange={(e: string) => {
                  void setValues({ ...values, launchUrl: e });
                }}
              />
              {errors.launchUrl && touched.launchUrl && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.launchUrl} />
                </Box>
              )}

              <FormLabel label={"ODS Code (optional)"} error={false} />
              <TextField
                name={"odsCode"}
                useFullWidth={true}
                value={values.odsCode ?? ""}
                onChange={(e: string) => {
                  void setValues({ ...values, odsCode: e });
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.rememberLoginEnabled ?? false}
                    onChange={() => {
                      void setValues({
                        ...values,
                        rememberLoginEnabled: !values.rememberLoginEnabled,
                      });
                    }}
                    data-testid="remember-login-enabled"
                  />
                }
                label={"Remember clinician sign-in (pre-fill Auth0 login hint)"}
              />
            </Box>

            <Box sx={{ mb: 2, mt: 1 }}>
              <Button
                label={genericInstance ? "Update" : "Create"}
                onClick={() => {
                  void submitForm();
                }}
                disabled={!dirty}
              />
              {genericInstance && (
                <DeleteGenericInstanceButton
                  genericInstance={genericInstance}
                />
              )}
            </Box>
          </>
        );
      }}
    </Formik>
  );
}

function GenericInstanceEditPageInner({
  genericInstanceId,
  genericInstance,
  refetchGenericInstance,
}: {
  genericInstanceId: string;
  genericInstance: GenericConfiguration | null;
  refetchGenericInstance: () => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {genericInstance ? (
        <h2>{genericInstance.displayName}</h2>
      ) : (
        <h2>Create New Generic Instance</h2>
      )}
      <GenericInstanceEdit
        genericInstanceId={genericInstanceId}
        genericInstance={genericInstance}
        refetchGenericInstance={refetchGenericInstance}
      />
    </Box>
  );
}

export function GenericInstanceCreateEdit() {
  const { genericInstanceId } = useParams();
  const navigate = useNavigate();
  const [
    { data: genericInstance, loading: genericInstanceLoading, response },
    refetchGenericInstance,
  ] = apiHooks.generic.get(genericInstanceId ?? "");

  if (!genericInstanceId) {
    return <></>;
  }

  return genericInstanceLoading ? (
    <></>
  ) : (
    <>
      <GenericInstanceEditPageInner
        genericInstanceId={genericInstanceId}
        genericInstance={
          response?.status === 200 ? (genericInstance ?? null) : null
        }
        refetchGenericInstance={() => {
          void refetchGenericInstance();
        }}
      />
      <Box sx={{ mb: 2, mt: 1 }}>
        <Button
          label={"Back"}
          onClick={() => {
            navigate(routeFns.adminGenericInstancesPage());
          }}
        />
      </Box>
    </>
  );
}
